import { IconButton, FormHelperText, styled } from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import { normalizeFileName } from "../utils/utils";
import { ChangeEvent, useCallback } from "react";

import DynamicIcon from "./dynamic_icon";
import PrimaryButton from "./primary_button";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * DefaultFormFileInput
 */

type Props<F extends {}> = {
  name: keyof F;
  saving: boolean;
  loading: boolean;
  filename?: string;
  maxFilenameLength?: number;
  reactHookForm: UseFormReturn<F>;
  onDownload?: (filename: string) => void;
  onChangeFile: () => void;
  onLimparArquivo: () => void;
};

export const DefaultFormFileInput = <F extends {},>({
  name,
  saving,
  loading,
  filename,
  maxFilenameLength = 40,
  reactHookForm,
  onDownload,
  onChangeFile,
  onLimparArquivo,
}: Props<F>) => {

  const handleDownload = useCallback(() => {
    if (!saving && !loading && filename !== undefined) {
      onDownload?.(filename);
    }
  }, [
    saving,
    loading,
    filename,
    onDownload,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        onLimparArquivo();
      }
    }
  }, [
    saving,
    onLimparArquivo,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && !loading && e.target.files && e.target.files.length > 0) {
      onChange(e.target.files.item(0));
      onChangeFile();
    }
  }, [
    saving,
    loading,
    onChangeFile,
  ]);

  return (
    <div className="flex w-full">
      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (name in formState.errors) {
            const error = formState.errors[name] as any;
            if (typeof error.message === 'string') {
              helperText = error.message;
            }
          }

          return (filename !== undefined && filename.length > 0) ? (
            <div className="w-full flex flex-row justify-between items-center">
              <span
                className="cursor-pointer text-black dark:text-white"
                onMouseDown={handleDownload}>
                {filename && filename.length > maxFilenameLength - 4
                  ? `${filename.substring(0, maxFilenameLength - 4)}...${filename.substring(filename.length - 4)}`
                  : filename}
              </span>
              <IconButton onClick={handleDeleteFile}>
                <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
              </IconButton>
            </div>
          ) : (
            <div className="w-full flex flex-col">
              <PrimaryButton
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                disabled={saving}
                startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                {field.value && field.value instanceof File ? normalizeFileName(field.value.name, maxFilenameLength) : "Escolher arquivo"}
                <VisuallyHiddenInput
                  type="file"
                  onChange={(e) => handleFileChange(e, field.onChange)}
                  multiple={false} />
              </PrimaryButton>

              <FormHelperText
                id={`${name as any}-component-error-text`}
                error={helperText !== undefined}>
                {helperText}
              </FormHelperText>
            </div>
          );
        }} />
    </div>
  );
}